/**
 * The board chrome's two entry points into M6-A2.
 *
 * `useAutomationsSource` builds the tRPC-backed port once per client; `useAutomationsBadge`
 * answers the header's `Automate / N`, whose N is the **active** count — the same number the
 * modal's Manage tab is badged with (`ui-reference/automations/INDEX.md`).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPCClient } from '../../lib/trpc';
import { createAutomationsSource, type AutomationsTrpc } from './trpc-source';
import type { AutomationsSource } from './types';

export function useAutomationsSource(): AutomationsSource {
  const client = useTRPCClient();
  return useMemo(() => createAutomationsSource(client as unknown as AutomationsTrpc), [client]);
}

export const automationsBadgeKey = (boardId: string): unknown[] => ['automations', 'badge', boardId];

export function useAutomationsBadge(boardId: string | null) {
  const source = useAutomationsSource();
  return useQuery({
    queryKey: automationsBadgeKey(boardId ?? '0'),
    queryFn: async () => {
      const rows = await source.list({ boardId: boardId ?? '0' });
      return rows.filter((row) => row.runnable).length;
    },
    enabled: boardId !== null,
    staleTime: 30_000,
  });
}

/** Called when the centre closes, so the header badge catches up with what was toggled. */
export function useInvalidateAutomationsBadge(): (boardId: string) => void {
  const queryClient = useQueryClient();
  return (boardId: string) => {
    void queryClient.invalidateQueries({ queryKey: automationsBadgeKey(boardId) });
  };
}
